/**
 * Web component: Order tracking (order-tracking)
 * -----------------------------------------------------------------------------
 * Order tracking powered by ParcelLab.
 *
 */
import classes from '~/config/classes';
import './order-tracking.scss';

import { vueReady } from '~/core/runners';
import { waitFor } from '~/helpers/general';

/**
 * DOM selectors.
 */
const selectors = {
  form: '[js-order-tracking="form"]',
  error: '.order-tracking__error',
};

/**
 * Create new <order-tracking> custom element.
 */
class OrderTracking extends HTMLElement {
  data(component) {
    return {
      selectors: {
        form: component.querySelector(selectors.form),
        error: component.querySelector(selectors.error),
      },
      localCourierRerouteLink: {
        DEU: 'https://www.dhl.de/de/privatkunden/pakete-empfangen/verfolgen.html?piececode={tracking_number}',
        BEL: 'https://track.bpost.cloud/btr/web/#/search?lang={language}&itemCode={tracking_number}&postalCode={zip_code}',
        DNK: 'https://www.postnord.dk/varktojer/track-trace?shipmentId={tracking_number}',
        FIN: 'https://www.posti.fi/en/tracking#/lahetys/{tracking_number}?lang=en',
        FRA: 'https://trace.dpd.fr/fr/trace/{tracking_number}',
        ITA: 'https://www.dhl.com/it-en/home/tracking/tracking-express.html?submit=1&tracking-id={tracking_number}',
        LUX: 'https://api.parcellab.com/deeplink/postlu?tracking_number={tracking_number}',
        NLD: 'https://www.dhlparcel.nl/nl/particulier/ontvangen/volg-uw-zending?tt={tracking_number}&pc={zip_code}',
        AUT: 'https://www.post.at/sv/sendungsdetails?snr={tracking_number}',
        PLD: 'https://www.dhl.com/pl-en/home/tracking/tracking-parcel.html?tracking-id={tracking_number}',
        PRT: 'https://clientesparcel.dhl.es/LiveTracking/ModificarEnvio/{tracking_number}',
        SWE: 'https://www.postnord.se/vara-verktyg/spara-brev-paket-och-pall?shipmentId={tracking_number}',
        CHE: 'https://myparcel.ch.dhl.com/index.php?PID=446800535910&LANG=DE',
        ESP: 'https://clientesparcel.dhl.es/LiveTracking/ModifyMyShipment/{tracking_number}',
        CZE: 'https://www.postaonline.cz/trackandtrace/-/zasilka/cislo?parcelNumbers={tracking_number}',
        CYP: 'https://ips.cypruspost.gov.cy/ipswebtrack/IPSWeb_item_events.aspx?itemid={tracking_number}&Submit=Submit',
      },
    };
  }

  /**
   * Custom element constructor.
   * - Always call super first in constructor.
   */
  constructor() {
    super();
    this.$data = {};
  }

  /**
   * Lifecycle hook - component is attached to DOM.
   */
  connectedCallback() {
    this.$data = this.data(this);

    const parcellabParam = new URLSearchParams(location.search).get('s');
    const tokenParam = new URLSearchParams(location.search).get('token');
    if (parcellabParam && !tokenParam) {
      const url = new URL(location.href);
      url.searchParams.delete('s');
      url.searchParams.set('token', parcellabParam);
      location.replace(url.toString());
      return;
    }

    waitFor('parcelLabTrackAndTrace', () => {
      this.initializeTracking();
    });
  }

  /**
   * Initialize ParcelLab tracking.
   */
  initializeTracking() {
    let showError = false;

    const options = {
      client: this.dataset.clientId,
      containerId: `parcellab-track-and-trace-${this.dataset.sectionId}`,
      customTranslations: {
        [cnvs.store.language]: {
          searchOrder: this.dataset.orderNumberPlaceholder,
          zip: this.dataset.postcodePlaceholder,
        },
      },
      lang: cnvs.store.language,
      onRendered: this.onRenderedCallback.bind(this),
      plUserId: Number(this.dataset.userId),
    };

    if (this.dataset.useToken === 'true') {
      const tokenParam = new URLSearchParams(location.search).get('token');

      if (tokenParam) {
        options.s = tokenParam;
      } else {
        showError = true;
      }
    }

    const orderNoParam = new URLSearchParams(location.search).get('orderNo');

    if (orderNoParam && !showError) {
      options.show_articleList = true;
      options.disableVoting = true;
      options.rerouteButton = 'right';

      const linkTag = document.createElement('link');
      linkTag.rel = 'stylesheet';
      linkTag.href = 'https://cdn.parcellab.com/css/v5/main.min.css';
      document.getElementsByTagName('head')[0].appendChild(linkTag);

      this.$data.selectors.form.classList.remove(classes.loading);

      window.parcelLabTrackAndTrace.initialize(options);
    } else {
      this.$data.selectors.form.classList.remove(classes.loading);
      this.$data.selectors.form.classList.add(classes.hidden);
      this.$data.selectors.error.classList.remove(classes.hidden);
    }
  }

  /**
   * Callback function for when ParcelLab finishes rendering the tracked order.
   * @param {Object} pluginState - The plugin state object from ParcelLab.
   */
  onRenderedCallback(pluginState) {
    const { checkpoints, activeTracking } = pluginState;
    const courier = checkpoints.header[activeTracking].courier?.name;
    const country =
      checkpoints.header[activeTracking].delivery_info
        ?.destination_country_iso3;
    const zipCode = checkpoints.header[activeTracking].delivery_info?.zip_code;
    const trackingNumber = checkpoints.header[activeTracking]?.tracking_number;

    if (
      courier === 'dhl-germany' &&
      this.$data.localCourierRerouteLink[country]
    ) {
      const courierDeeplink = document.querySelector('#pl-courier-fwd-link');
      const courierRerouteLink = document.querySelector('#pl-reroute-link');

      if (courierDeeplink) {
        courierDeeplink.href = this.$data.localCourierRerouteLink[country]
          .replace('{tracking_number}', trackingNumber)
          .replace('{zip_code}', zipCode);
      }

      if (courierRerouteLink) {
        courierRerouteLink.href = this.$data.localCourierRerouteLink[country]
          .replace('{tracking_number}', trackingNumber)
          .replace('{zip_code}', zipCode);
      }
    }
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('order-tracking')) {
    return;
  }

  customElements.define('order-tracking', OrderTracking);
});
